import React, { useMemo, useState } from 'react';

import {
  Alert,
  Box,
  MenuItem,
  Select,
  Snackbar,
  FormControl
} from '@mui/material';

import {
  EditOutlined,
  SendOutlined,
  VisibilityOutlined,
  KeyboardArrowDownOutlined
} from '@mui/icons-material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';

import CommonTable from '../Common/components/Listing/CommonTable';
import ListingToolbar from '../Common/components/Listing/ListingToolbar';
import StatusChips from '../Common/components/Listing/StatusChips';

import { createStatusItems } from '../Common/components/Listing/statusConfig';

import CreateIssueModal from './components/CreateTaskModal';
import NotifyPatientModal from './components/NotifyPatientModal';
import ProviderResponseModal from './components/ProviderResponseModal';
import WorkflowTimelineModal from './components/WorkflowTimelineModal';

import {
  patientPageContentSx,
  patientPageRootSx
} from '../Common/components/Styles/PatientPageStyles';
import PageHeaderTabs from '../Common/components/PageHeaderTabs';

import {
  communicationRows,
  providerOptions,
  statusOptions
} from './components/data';

import { filterSelectSx } from './styles';
import {
  menuProps,
  textFieldSx
} from '../Common/components/Styles/InputStyles';

const statusItems = createStatusItems({
  allValue: 'All',
  awaitingLabel: 'Awaiting Provider',
  awaitingValue: 'Awaiting Provider',
  notificationLabel: 'Notify Patient',
  notificationValue: 'Notify Patient',
  completedValue: 'Completed'
});

const statusColor = {
  'Awaiting Provider': '#F44336',
  'Notify Patient': '#1976D2',
  Completed: '#2E7D32'
};

const columns = [
  {
    field: 'patient',
    headerName: 'Patient',
    width: '20%',
    primary: true
  },
  {
    field: 'category',
    headerName: 'Category',
    width: '20%'
  },
  {
    field: 'routeTo',
    headerName: 'Route To',
    width: '22%'
  },
  {
    field: 'priority',
    headerName: 'Priority',
    width: '14%'
  },
  {
    field: 'status',
    headerName: 'Status',
    width: '18%',
    cellSx: value => ({
      color: statusColor[value] || '#424242',
      fontWeight: 500
    })
  }
];

const Communication = () => {
  const [search, setSearch] = useState('');
  const [provider, setProvider] = useState('All Providers');
  const [status, setStatus] = useState('All');
  const [activeTab, setActiveTab] = useState('All');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [selectedRow, setSelectedRow] = useState(null);

  const [openCreateIssue, setOpenCreateIssue] = useState(false);
  const [openProviderResponse, setOpenProviderResponse] = useState(false);
  const [openNotifyPatient, setOpenNotifyPatient] = useState(false);
  const [openTimeline, setOpenTimeline] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return communicationRows.filter(row => {
      const matchesSearch =
        !normalizedSearch ||
        Object.values(row).some(value =>
          String(value)
            .toLowerCase()
            .includes(normalizedSearch)
        );

      const matchesProvider =
        provider === 'All Providers' || row.routeTo === provider;

      const matchesStatus = status === 'All' || row.status === status;

      const matchesTab = activeTab === 'All' || row.status === activeTab;

      return matchesSearch && matchesProvider && matchesStatus && matchesTab;
    });
  }, [search, provider, status, activeTab]);

  const visibleRows = useMemo(() => {
    const startIndex = page * rowsPerPage;

    return filteredRows.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const resetPage = () => {
    setPage(0);
  };

  const handleActionClick = row => {
    setSelectedRow(row);

    if (row.status === 'Awaiting Provider') {
      setOpenProviderResponse(true);
      return;
    }

    if (row.status === 'Notify Patient') {
      setOpenNotifyPatient(true);
      return;
    }

    setOpenTimeline(true);
  };

  return (
    <>
      <Box sx={patientPageRootSx}>
        <PageHeaderTabs type="title" title="Provider Communications" />

        <Box sx={patientPageContentSx}>
          <ListingToolbar
            search={search}
            searchPlaceholder="Search by patient, category, provider, or status..."
            createLabel="Create Issue"
            onSearchChange={value => {
              setSearch(value);
              resetPage();
            }}
            onCreate={() => setOpenCreateIssue(true)}>
            <FormControl size="small" sx={{ ...textFieldSx, width: '21%' }}>
              <Select
                value={provider}
                IconComponent={KeyboardArrowDownOutlined}
                onChange={event => {
                  setProvider(event.target.value);
                  resetPage();
                }}
                MenuProps={menuProps}>
                {providerOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ ...textFieldSx, width: '21%' }}>
              <Select
                IconComponent={KeyboardArrowDownOutlined}
                value={status}
                onChange={event => {
                  setStatus(event.target.value);
                  resetPage();
                }}
                MenuProps={menuProps}
                sx={{
                  ...filterSelectSx
                }}>
                {statusOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </ListingToolbar>

          <StatusChips
            items={statusItems}
            rows={communicationRows}
            activeValue={activeTab}
            onChange={value => {
              setActiveTab(value);
              resetPage();
            }}
          />

          <CommonTable
            columns={columns}
            rows={visibleRows}
            totalCount={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 75, 100]}
            emptyText="No communication records found."
            onPageChange={(event, newPage) => {
              setPage(newPage);
            }}
            onRowsPerPageChange={event => {
              setRowsPerPage(Number(event.target.value));
              resetPage();
            }}
            onActionClick={handleActionClick}
            actionWrapper
            loading={loading}
            skeletonRows={10}
            renderAction={row => {
              if (row.status === 'Completed') {
                return <VisibilityOutlined fontSize="small" />;
              }

              if (row.status === 'Notify Patient') {
                return <SendOutlined fontSize="small" />;
              }

              return <EditOutlined fontSize="small" />;
            }}
          />
        </Box>
      </Box>

      <CreateIssueModal
        open={openCreateIssue}
        onClose={() => setOpenCreateIssue(false)}
        onCreate={() => {
          setOpenCreateIssue(false);
          setOpenToast(true);
        }}
      />

      <ProviderResponseModal
        open={openProviderResponse}
        data={selectedRow}
        onClose={() => setOpenProviderResponse(false)}
      />

      <NotifyPatientModal
        open={openNotifyPatient}
        data={selectedRow}
        onClose={() => setOpenNotifyPatient(false)}
      />

      <WorkflowTimelineModal
        open={openTimeline}
        data={selectedRow}
        onClose={() => setOpenTimeline(false)}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={3000}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        onClose={() => setOpenToast(false)}>
        <Alert
          icon={
            <CheckCircleOutlineIcon
              sx={{
                color: '#fff'
              }}
            />
          }
          onClose={() => setOpenToast(false)}
          sx={{
            width: 500,
            bgcolor: '#4CAF50',
            color: '#fff',
            borderRadius: '4px',
            boxShadow: 'none',
            fontSize: 14,
            alignItems: 'center',

            '& .MuiAlert-message': {
              py: 0.5
            },

            '& .MuiAlert-action': {
              color: '#fff'
            }
          }}>
          A new issue has been created.
        </Alert>
      </Snackbar>
    </>
  );
};

export default Communication;
